import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { getEmbeddedResource, getUrl } from '@ngxp/rest';
import { ApiRootFacade, apiRootSelector } from '@schir-int-client/api-root';
import { ClipboardService } from '@schir-int-client/tech';
import { loadVerfahrenSingleSuccessAction, VerfahrenFacade } from '@schir-int-client/verfahren-shared';
import { map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import {
	createVorgangInVerfahrenAction,
	createVorgangInVerfahrenSuccessAction,
	deleteVorgangAction,
	deleteVorgangSuccessAction,
	loadFristenListeAction,
	loadFristenListeSuccessAction,
	loadVorgaengeByVerfahrenSuccessAction,
	loadVorgaengeInRuecklaufAction,
	loadVorgaengeInRuecklaufSuccessAction,
	loadVorgaengeInWiedervorlageAction,
	loadVorgaengeInWiedervorlageSuccessAction,
	loadVorgaengeRechtspflegeAction,
	loadVorgaengeRechtspflegeSuccessAction,
	loadVorgangAction,
	loadVorgangSuccessAction,
	setVorgangStatusAction,
	setVorgangStatusSuccessAction,
	updateVorgangNotizAction,
	updateVorgangNotizSuccessAction,
} from './vorgang.actions';
import { VorgangListLinkRel } from './vorgang.linkrel';
import { VorgangListResource, VorgangResource } from './vorgang.model';
import { VorgangService } from './vorgang.service';
import { AppState } from '../../../../apps/int-client/src/app/shared/store/app-state.model';
import { deleteVerfuegungSuccessAction } from '@schir-int-client/verfuegung-shared';

@Injectable()
export class VorgangEffects {
	constructor(
		private actions: Actions,
		private vorgangService: VorgangService,
		private store: Store<AppState>,
		private verfahrenFacade: VerfahrenFacade,
		private apiRootFacade: ApiRootFacade,
		private clipboardService: ClipboardService
	) {}

	createVorgangInVerfahren = createEffect(() =>
		this.actions.pipe(
			ofType(createVorgangInVerfahrenAction),
			withLatestFrom(this.verfahrenFacade.verfahrenSingle),
			switchMap(([{ vorgangTyp, uri }, verfahren]) => {
				return this.vorgangService
					.createVorgangInVerfahren(uri, vorgangTyp)
					.pipe(map(vorgaenge => createVorgangInVerfahrenSuccessAction({ vorgaenge, verfahren })));
			})
		)
	);

	loadVorgaengeByVerfahren = createEffect(() =>
		this.actions.pipe(
			ofType(loadVerfahrenSingleSuccessAction, deleteVorgangSuccessAction),
			withLatestFrom(this.verfahrenFacade.verfahrenSingle),
			switchMap(([, verfahren]) => {
				return this.vorgangService
					.getVorgaengeByVerfahren(verfahren)
					.pipe(map(vorgaenge => loadVorgaengeByVerfahrenSuccessAction({ vorgaenge, verfahren })));
			})
		)
	);

	loadVorgang = createEffect(() =>
		this.actions.pipe(
			ofType(loadVorgangAction),
			switchMap(({ vorgangUri }) => {
				return this.vorgangService
					.getOne(vorgangUri)
					.pipe(map(vorgang => loadVorgangSuccessAction({ vorgang })));
			})
		)
	);

	reloadVorgang = createEffect(() =>
		this.actions.pipe(
			ofType(deleteVerfuegungSuccessAction),
			withLatestFrom(this.store.select(apiRootSelector)),
			map(([{ deleted }, apiRoot]) =>
				loadVorgangAction({ vorgangUri: getUrl(apiRoot) + '/vorgangs/' + deleted.vorgangId })
			)
		)
	);

	loadVorgaengeRechtspflege = createEffect(() =>
		this.actions.pipe(
			ofType(loadVorgaengeRechtspflegeAction),
			withLatestFrom(this.store.select(apiRootSelector)),
			switchMap(([, apiRoot]) => {
				return this.vorgangService.getAllForRechtspflege(apiRoot).pipe(
					map(vorgaengeList =>
						loadVorgaengeRechtspflegeSuccessAction({
							vorgaenge: getEmbeddedResource(vorgaengeList, VorgangListLinkRel.VORGANG_LIST),
						})
					)
				);
			})
		)
	);

	loadVorgaengeInRuecklauf = createEffect(() =>
		this.actions.pipe(
			ofType(loadVorgaengeInRuecklaufAction),
			withLatestFrom(this.apiRootFacade.apiRoot),
			switchMap(([, apiRoot]) => {
				return this.vorgangService
					.loadVorgaengeInRuecklauf(apiRoot)
					.pipe(map(vorgaenge => loadVorgaengeInRuecklaufSuccessAction({ vorgaenge })));
			})
		)
	);

	updateNotiz = createEffect(() =>
		this.actions.pipe(
			ofType(updateVorgangNotizAction),
			switchMap(({ vorgang, notiz }) => {
				return this.vorgangService
					.updateNotiz(notiz, vorgang)
					.pipe(map(vorgang => updateVorgangNotizSuccessAction({ vorgang })));
			})
		)
	);

	setVorgangStatus = createEffect(() =>
		this.actions.pipe(
			ofType(setVorgangStatusAction),
			switchMap(({ vorgang, status, wiedervorlageDatum }) => {
				return this.vorgangService
					.setVorgangStatus(vorgang, status, wiedervorlageDatum)
					.pipe(map(vorgang => setVorgangStatusSuccessAction({ vorgang })));
			})
		)
	);

	//TODO: mit SCHIFF-5169 aktivieren und Logik aus den Component entfernen

	// markAsSuccessReload = createEffect(() => this.actions.pipe(
	// 	ofType(VorgangActions.MARKED_SUCCESS),
	// 	switchMap((action: VorgangMarkedSuccessAction) => {
	// 		const status = action.vorgang.status;
	//
	// 		switch (status) {
	// 			case VorgangStatus.RUECKLAUF:
	// 				return of(new LoadVorgaengeInRuecklaufAction());
	// 			case VorgangStatus.RECHTSPFLEGE:
	// 				return of(new LoadVorgaengeRechtspflegeAction());
	// 		}
	// 	})
	// ));

	delete = createEffect(() =>
		this.actions.pipe(
			ofType(deleteVorgangAction),
			switchMap(({ vorgang }) => {
				return this.vorgangService
					.delete(vorgang)
					.pipe(map(() => deleteVorgangSuccessAction({ uri: vorgang._links.self.href })));
			})
		)
	);

	loadVorgaengeInWiedervorlage = createEffect(() =>
		this.actions.pipe(
			ofType(loadVorgaengeInWiedervorlageAction),
			withLatestFrom(this.apiRootFacade.apiRoot),
			switchMap(([{ von, bis }, apiRoot]) => {
				return this.vorgangService
					.getAllInWiedervorlage(apiRoot, von, bis)
					.pipe(map(wiedervorlagen => loadVorgaengeInWiedervorlageSuccessAction({ wiedervorlagen })));
			})
		)
	);

	loadFristenListe = createEffect(() =>
		this.actions.pipe(
			ofType(loadFristenListeAction),
			withLatestFrom(this.apiRootFacade.apiRoot),
			switchMap(([, apiRoot]) => {
				return this.vorgangService.getFristenListe(apiRoot).pipe(
					tap(resource => this.copyFristenListeToClipboard(resource)),
					map(fristenListe => loadFristenListeSuccessAction({ fristenListe }))
				);
			})
		)
	);

	copyFristenListeToClipboard(value: VorgangListResource) {
		let text = '';
		const vorgaenge: VorgangResource[] = getEmbeddedResource(value, VorgangListLinkRel.VORGANG_LIST);

		if (vorgaenge && vorgaenge.length > 0) {
			vorgaenge.forEach(vorgang => {
				text = text.concat(vorgang.aktenzeichen + '\n');
			});

			this.clipboardService.copyAndShowSnackBar(text, 'Die Fristenliste wurde in die Zwischenablage kopiert.');
		}
	}
}
